"use client";

import { useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { calculatePremium, createRecommendation } from "@/api/simpego";
import { ProductVariant } from "@/hooks/useFunnelState";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { Address, ContactDetails, Customer, OfferRequest, OfferResponse, PremiumCorrection } from "@/types/simpego-api";
import { BASE_COVERAGES, BaseCoverageId } from "@/config/coverages";
import { CoverageState, buildCoveragesPayload, buildContractCoveragesPayload, extractCoveragePremiums, extractCoverageStateFromOffer, extractDeclinedCoverages, getOfferCoverage, isCoverageStateValid } from "@/utils/coveragePayload";
import { getCustomerErrorMessage, isHandledValidationError } from "@/utils/apiError";
import * as Sentry from "@sentry/react";

interface UsePremiumCalculationProps {
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  selectedProduct: ProductVariant | null;
  /** True once a plan exists (a chosen bundle, or an offer the customer jumped in with — which has no bundle). */
  hasSelectedPlan: boolean;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  coverageDeductiblesOtherBand: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  annualDeductible: number | null;
  dailyTermination: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  contractId: string | null;
  contractToken: string | null;
  agentId: string | null; // New prop for agentId
  currentLocale: string;
  whitelabelPartner: string | null;
  premiumCorrections: PremiumCorrection[] | null;
}

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const usePremiumCalculation = ({
  petType,
  petName,
  breed,
  birthDate,
  selectedBreedType,
  gender,
  isCastrated,
  isHouseCat,
  selectedDogSize,
  selectedProduct,
  hasSelectedPlan,
  coverageSelections,
  subCoverageSelections,
  subCoverageSumInsured,
  coverageDeductibles,
  coverageDeductiblesOtherBand,
  coverageSumInsured,
  coverageSumInsuredPerCase,
  emergencyCareSumInsuredPerDay,
  annualDeductible,
  dailyTermination,
  paymentInterval,
  paymentMethod,
  startOfInsurance,
  contractId,
  contractToken,
  agentId, // Destructure new prop
  whitelabelPartner,
  premiumCorrections,
  currentLocale,
}: UsePremiumCalculationProps) => {
  const [underwritingFailed, setUnderwritingFailed] = useState<boolean>(false);
  const [underwritingReason, setUnderwritingReason] = useState<string | null>(null);
  const { showErrorDialog } = useErrorDialog();

  // Every keystroke on a coverage field changes the queryKey and so fires its own request, so concurrent in-flight requests aren't deduped/cancelled —
  // a stale response for an earlier (e.g. still-declined) value can resolve after a newer
  // (now-accepted) one and clobber `declinedCoverages`/`declinedSubs`/etc via these plain
  // `useState` setters. Only the response matching the most recently *started* request is
  // allowed to apply those side effects, so badges can't get stuck showing an outdated verdict.
  const latestRequestIdRef = useRef(0);

  const DEFAULT_AGENT_ID = typeof window !== 'undefined' && (window as any).__env__?.defaultAgentId ? (window as any).__env__.defaultAgentId : "VA0000000003";

  // Pet 2.0 has no product variants and the backend always works with "L" (SIP-4289), so an offer the
  // customer jumped in with (no bundle chosen) is priced with that default.
  const selectedBundle = selectedProduct ? (selectedProduct.id.toUpperCase() as "S" | "M" | "L") : "L";

  const buildOfferRequest = useCallback((
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    productBundle: "S" | "M" | "L" | "individual",
    coverageState: CoverageState,
    dailyTerminationArg: boolean,
    paymentIntervalArg: 1 | 2 | 4 | 12,
    paymentMethodArg: "credit_card" | "ESR",
    startOfInsuranceArg: Date,
    locale: string,
    agentIdArg: string | null
  ): OfferRequest => {
    const formattedPetBirthdate = formatDateLocal(birthDateArg);
    const safeStartOfInsurance = startOfInsuranceArg ?? new Date();
    const formattedStartOfInsurance = formatDateLocal(safeStartOfInsurance);
    const customerBirthdate = "1980-01-01";

    let finalBreed: string | null = null;
    let isCrossbreedFlag = false;

    if (selectedBreedTypeArg === "purebred" || selectedBreedTypeArg === "crossbreed") {
      finalBreed = breedArg;
      isCrossbreedFlag = selectedBreedTypeArg === "crossbreed";
    } else if (selectedBreedTypeArg === "mixed") {
      isCrossbreedFlag = false;
      if (petTypeArg === "cat") {
        finalBreed = "mischlingskatze";
      } else if (petTypeArg === "dog") {
        finalBreed = selectedDogSizeArg === "small" ? "mischling_klein" : "mischling_gross";
      }
    }

    const dummyCustomerAddress: Address = {
      street: "Musterstrasse",
      housenumber: "1",
      zip: "8000",
      city: "Zürich",
      countryCode: "CH",
    };

    const dummyCustomerContactDetails: ContactDetails[] = [
      { value: "dummy@example.com", category: "email" }
    ];

    const dummyCustomer: Customer = {
      objectType: "NaturalPerson",
      firstname: "Max",
      name: "Muster",
      birthdate: customerBirthdate,
      email: "dummy@example.com",
      gender: "male",
      language: locale.toUpperCase() as "DE" | "FR" | "IT" | "EN",
      address: dummyCustomerAddress,
      contactDetails: dummyCustomerContactDetails,
    };

    return {
      contractID: contractId || undefined,
      contractToken: contractToken || undefined,
      startOfInsurance: formattedStartOfInsurance,
      documentDispatch: {
        dispatch: "electronic",
      },
      dailyTermination: { selected: dailyTerminationArg },
      paymentMethod: paymentMethodArg ?? "credit_card",
      yearlyPaymentFrequency: {
        frequency: paymentIntervalArg ?? 12,
      },
      agentID: agentIdArg || DEFAULT_AGENT_ID,
      ...(whitelabelPartner ? { whitelabelPartner } : {}),
      product: {
        productName: "pet",
        ...(premiumCorrections?.length ? { productControl: { premiumCorrections } } : {}),
        contractCoverages: buildContractCoveragesPayload(coverageState),
        pets: [
          {
            species: petTypeArg === "dog" ? "Dog" : "Cat",
            name: petNameArg,
            birthdate: formattedPetBirthdate,
            breed: finalBreed,
            crossbreed: isCrossbreedFlag,
            gender: genderArg,
            castration: isCastratedArg,
            housecat: petTypeArg === "cat" ? isHouseCatArg : undefined,
            productBundle,
            coverages: buildCoveragesPayload(coverageState, birthDateArg),
          },
        ],
      },
      customer: dummyCustomer,
    };
  }, [contractId, contractToken, DEFAULT_AGENT_ID, whitelabelPartner, premiumCorrections]);

  const fetchOffer = useCallback(async (
    payload: OfferRequest,
    call: (payload: OfferRequest) => Promise<OfferResponse>,
    // Batched callers (e.g. fetchBundleRecommendations' parallel S/M/L calls) show a single
    // aggregate dialog themselves — letting each parallel call pop its own dialog here would
    // have them stomp on the same dialog's single resolveRef.
    silent = false
  ): Promise<{ offer: OfferResponse | null; failed: boolean; reason: string | null }> => {
    try {
      const response = await call(payload);
      if (response.underwriting === false) {
        const reason = response.underwritingReason || response.feedbackReason || "Unknown underwriting reason.";
        return { offer: null, failed: true, reason };
      }
      return { offer: response, failed: false, reason: null };
    } catch (error) {
      if (!isHandledValidationError(error)) Sentry.captureException(error);
      if (!silent) await showErrorDialog(getCustomerErrorMessage(error));
      return { offer: null, failed: true, reason: getCustomerErrorMessage(error) };
    }
  }, [showErrorDialog]);

  /** Fetches a live recommendation per bundle tier (S/M/L). Sends only the `requested`
   * coverage flags picked on step 1 — no manual sums/deductibles — the backend recommends
   * the actual figures for each tier. On failure, shows one aggregate error dialog and retries
   * the whole batch once the customer dismisses it (`shouldContinue` lets the caller cancel
   * that retry, e.g. on unmount/navigation, instead of retrying forever). */
  const fetchBundleRecommendations = useCallback(async (
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date | undefined,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    coverageSelectionsArg: Partial<Record<BaseCoverageId, boolean>>,
    locale: string,
    agentIdArg: string | null,
    startOfInsuranceArg: Date,
    shouldContinue: () => boolean = () => true
  ): Promise<{ S: OfferResponse; M: OfferResponse; L: OfferResponse } | null> => {
    if (!petTypeArg || !petNameArg || !breedArg || !birthDateArg || !selectedBreedTypeArg || !genderArg) {
      console.log("Missing data for recommendation fetch.");
      return null;
    }

    const bundles: ("S" | "M" | "L")[] = ["S", "M", "L"];

    while (shouldContinue()) {
      const outcomes = await Promise.all(
        bundles.map((bundle) => {
          const payload = buildOfferRequest(
            petTypeArg,
            petNameArg,
            breedArg,
            birthDateArg,
            selectedBreedTypeArg,
            genderArg,
            isCastratedArg,
            isHouseCatArg,
            selectedDogSizeArg,
            bundle,
            { coverageSelections: coverageSelectionsArg, subCoverageSelections: {}, subCoverageSumInsured: {}, coverageDeductibles: {}, coverageSumInsured: {}, coverageSumInsuredPerCase: {}, emergencyCareSumInsuredPerDay: null, annualDeductible: null },
            true, // Päckli sind mit täglichem Kündigungsrecht vorbelegt; das Backend gibt den gesendeten Wert zurück
            12,
            "credit_card",
            startOfInsuranceArg,
            locale,
            agentIdArg
          );
          return fetchOffer(payload, createRecommendation, true).then((outcome) => ({ bundle, ...outcome }));
        })
      );

      const results: Partial<Record<"S" | "M" | "L", OfferResponse>> = {};
      let failureReason: string | null = null;
      for (const outcome of outcomes) {
        if (outcome.failed || !outcome.offer) {
          failureReason = failureReason ?? outcome.reason;
          continue;
        }
        results[outcome.bundle] = outcome.offer;
      }

      if (!results.S || !results.M || !results.L) {
        await showErrorDialog(failureReason || getCustomerErrorMessage(null));
        setUnderwritingFailed(false);
        setUnderwritingReason(null);
        continue;
      }

      setUnderwritingFailed(false);
      setUnderwritingReason(null);
      return { S: results.S, M: results.M, L: results.L };
    }

    return null;
  }, [buildOfferRequest, fetchOffer, showErrorDialog]);

  /** Fetches a live recommendation for exactly one newly-added coverage on step 3. Strips
   * that coverage's own deductible/subs/sumInsured overrides from the request (letting the
   * backend recommend fresh figures for it) while every other already-configured coverage is
   * sent as-is — so the caller can merge back only the target coverage's recommended fields
   * without disturbing anything the customer already customized elsewhere. Returns the full
   * extracted CoverageState (caller picks out the relevant slice); null on missing data or a
   * failed/declined call. */
  const fetchCoverageRecommendation = useCallback(async (
    targetCoverageId: BaseCoverageId,
    currentCoverageState: CoverageState
  ): Promise<CoverageState | null> => {
    if (!petType || !petName || !breed || !birthDate || !selectedBreedType || !gender || !hasSelectedPlan) {
      return null;
    }

    const requestState: CoverageState = {
      ...currentCoverageState,
      coverageDeductibles: { ...currentCoverageState.coverageDeductibles, [targetCoverageId]: undefined },
      coverageDeductiblesOtherBand: { ...currentCoverageState.coverageDeductiblesOtherBand, [targetCoverageId]: undefined },
      subCoverageSelections: { ...currentCoverageState.subCoverageSelections, [targetCoverageId]: undefined },
      subCoverageSumInsured: { ...currentCoverageState.subCoverageSumInsured, [targetCoverageId]: undefined },
      coverageSumInsured: { ...currentCoverageState.coverageSumInsured, [targetCoverageId]: undefined },
      coverageSumInsuredPerCase: { ...currentCoverageState.coverageSumInsuredPerCase, [targetCoverageId]: undefined },
      emergencyCareSumInsuredPerDay: targetCoverageId === "emergencyCare" ? null : currentCoverageState.emergencyCareSumInsuredPerDay,
      // Shared by Krankheit and Unfall, so (re)enabling either line lets the backend recommend it afresh.
      annualDeductible: targetCoverageId === "disease" || targetCoverageId === "accident" ? null : currentCoverageState.annualDeductible,
    };

    const payload = buildOfferRequest(
      petType,
      petName,
      breed,
      birthDate,
      selectedBreedType,
      gender,
      isCastrated,
      isHouseCat,
      selectedDogSize,
      selectedBundle,
      requestState,
      dailyTermination,
      paymentInterval,
      paymentMethod,
      startOfInsurance ?? new Date(),
      currentLocale || "en",
      agentId
    );

    try {
      const offer = await createRecommendation(payload);
      if (offer.underwriting === false) return null;
      return extractCoverageStateFromOffer(offer);
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  }, [
    petType, petName, breed, birthDate, selectedBreedType, gender, isCastrated, isHouseCat, selectedDogSize,
    selectedBundle, hasSelectedPlan, buildOfferRequest, dailyTermination, paymentInterval, paymentMethod, startOfInsurance,
    currentLocale, agentId,
  ]);

  // Per-coverage Teilprämie (gross, pro rata) read off the same live step-3 offer that
  // produces `currentApiPrice` — kept as separate state since react-query's `data` here is
  // just the total price number, not the full offer.
  // The offer the current price comes from: the cart and the summary list its coverages like the comparison table.
  const [currentOffer, setCurrentOffer] = useState<OfferResponse | null>(null);
  const [coveragePremiums, setCoveragePremiums] = useState<Partial<Record<BaseCoverageId, number>>>({});

  // Sub-coverages the customer selected that the backend declined on the last live price
  // calculation (openapi's SubTreatment carries no underwriting/feedback fields of its own,
  // so a discrepancy between what we sent as `requested: true` and what came back is the only
  // signal available) — surfaced next to the sub as a "not insurable" hint instead of the
  // toggle silently no longer matching what's actually being priced.
  const [declinedSubs, setDeclinedSubs] = useState<Partial<Record<BaseCoverageId, Record<string, boolean>>>>({});

  // The parent base coverage's own `underwritingReason` (openapi: only Disease/Accident/
  // PreexistingConditions carry this, never the SubTreatment itself) — the closest thing to a
  // real explanation the backend gives for why one of that coverage's subs got declined, so
  // it's captured whenever `declinedSubs` has an entry for that coverage and shown alongside it.
  const [coverageUnderwritingReasons, setCoverageUnderwritingReasons] = useState<Partial<Record<BaseCoverageId, string>>>({});

  // Base coverages the customer requested that the backend rejected outright (openapi:
  // `underwriting: false` on the coverage itself, e.g. "Prämie Baustein Vorerkrankung zu
  // hoch") — still comes back as `requested: true` with `netPremiumProRata: 0`, which reads as
  // a legitimately free coverage unless surfaced here instead.
  const [declinedCoverages, setDeclinedCoverages] = useState<Partial<Record<BaseCoverageId, string>>>({});

  // Use react-query for automatic refetching and loading state management (step-3 live re-price).
  const { isLoading, data: currentApiPrice } = useQuery({
    // Without this, `data` is `undefined` (not `null`) until the query's first successful
    // run — and now that `enabled` below can stay false for a while (invalid coverage input),
    // consumers checking `currentApiPrice !== null` (e.g. PlanSummary) would treat that
    // `undefined` as a real price and do arithmetic on it, producing "NaN / Month".
    initialData: null,
    queryKey: [
      "premium",
      petType,
      petName,
      breed,
      birthDate?.toISOString(),
      selectedBreedType,
      gender,
      isCastrated,
      isHouseCat,
      selectedDogSize,
      selectedProduct?.id,
      coverageSelections,
      subCoverageSelections,
      subCoverageSumInsured,
      coverageDeductibles,
      coverageDeductiblesOtherBand,
      coverageSumInsured,
      coverageSumInsuredPerCase,
      emergencyCareSumInsuredPerDay,
      annualDeductible,
      dailyTermination,
      paymentInterval,
      paymentMethod,
      startOfInsurance ? startOfInsurance.toISOString() : "no-date",
      contractId,
      contractToken,
      agentId,
      whitelabelPartner,
      premiumCorrections,
      currentLocale,
    ],
    queryFn: async () => {
      const requestId = ++latestRequestIdRef.current;
      const isLatestRequest = () => requestId === latestRequestIdRef.current;

      if (
        !petType ||
        !petName ||
        !breed ||
        !birthDate ||
        !selectedBreedType ||
        !gender ||
        !hasSelectedPlan
      ) {
        // If essential data is missing, we cannot calculate premium.
        if (isLatestRequest()) {
          setUnderwritingFailed(true);
          setUnderwritingReason("Missing essential pet or product details for premium calculation.");
          setCoveragePremiums({});
          setCurrentOffer(null);
          setDeclinedSubs({});
          setCoverageUnderwritingReasons({});
          setDeclinedCoverages({});
        }
        return null;
      }

      // Don't call the pricing API while the customer has an invalid/incomplete coverage
      // input (e.g. an out-of-range deductible, or a sum insured field mid-edit) — `enabled`
      // below skips the automatic fetch for this; the same check is kept here as a safety net in
      // case the queryFn is ever run directly (e.g. a manual refetch). Leaves the
      // previously-shown price/underwriting state untouched rather than clearing it, since an
      // invalid field is a normal mid-typing state, not a pricing failure.
      if (!isCoverageStateValid({ coverageSelections, subCoverageSelections, subCoverageSumInsured, coverageDeductibles, coverageSumInsured, coverageSumInsuredPerCase, emergencyCareSumInsuredPerDay, annualDeductible })) {
        return currentApiPrice ?? null;
      }

      const payload = buildOfferRequest(
        petType,
        petName,
        breed,
        birthDate,
        selectedBreedType,
        gender,
        isCastrated,
        isHouseCat,
        selectedDogSize,
        selectedBundle,
        { coverageSelections, subCoverageSelections, subCoverageSumInsured, coverageDeductibles, coverageDeductiblesOtherBand, coverageSumInsured, coverageSumInsuredPerCase, emergencyCareSumInsuredPerDay, annualDeductible },
        dailyTermination,
        paymentInterval,
        paymentMethod,
        startOfInsurance ?? new Date(),
        currentLocale || "en", // Default to en if locale missing
        agentId // Pass agentId
      );
      const { offer, failed, reason } = await fetchOffer(payload, calculatePremium);
      if (failed || !offer) {
        if (isLatestRequest()) {
          setUnderwritingFailed(true);
          setUnderwritingReason(reason);
          setCoveragePremiums({});
          setCurrentOffer(null);
          setDeclinedSubs({});
          setCoverageUnderwritingReasons({});
          setDeclinedCoverages({});
        }
        return null;
      }

      if (isLatestRequest()) {
        setUnderwritingFailed(false);
        setUnderwritingReason(null);
        setCurrentOffer(offer);
        setCoveragePremiums(extractCoveragePremiums(offer));
        setDeclinedCoverages(extractDeclinedCoverages(offer));

        const nextDeclinedSubs: Partial<Record<BaseCoverageId, Record<string, boolean>>> = {};
        const nextCoverageUnderwritingReasons: Partial<Record<BaseCoverageId, string>> = {};
        for (const def of BASE_COVERAGES) {
          if (def.subs.length === 0) continue;
          const requestedSubs = subCoverageSelections[def.id];
          if (!requestedSubs) continue;
          const cov = getOfferCoverage(offer, def);
          const declined: Record<string, boolean> = {};
          for (const sub of def.subs) {
            if (requestedSubs[sub.id] && !cov?.[sub.id]?.requested) {
              declined[sub.id] = true;
            }
          }
          if (Object.keys(declined).length > 0) {
            nextDeclinedSubs[def.id] = declined;
            if (typeof cov?.underwritingReason === "string") {
              nextCoverageUnderwritingReasons[def.id] = cov.underwritingReason;
            }
          }
        }
        setDeclinedSubs(nextDeclinedSubs);
        setCoverageUnderwritingReasons(nextCoverageUnderwritingReasons);
      }

      return offer.grossPremiumProRata ?? null;
    },
    // Enable automatic fetching only once all required pet/product data is present *and* the
    // customer's editable coverage fields are all valid — an invalid field (out-of-range
    // deductible, missing sum insured, etc.) shouldn't trigger a live pricing call.
    enabled: !!(petType && petName && breed && birthDate && selectedBreedType && gender && hasSelectedPlan) &&
      isCoverageStateValid({ coverageSelections, subCoverageSelections, subCoverageSumInsured, coverageDeductibles, coverageSumInsured, coverageSumInsuredPerCase, emergencyCareSumInsuredPerDay, annualDeductible }),
    onError: (error) => {
      console.error("Query error fetching premium:", error);
      Sentry.captureException(error);
      setUnderwritingFailed(true);
      setUnderwritingReason("Error during premium calculation query.");
    },
  });

  return {
    isLoading,
    fetchBundleRecommendations,
    fetchCoverageRecommendation,
    currentApiPrice,
    currentOffer,
    coveragePremiums,
    declinedSubs,
    coverageUnderwritingReasons,
    declinedCoverages,
    underwritingFailed,
    underwritingReason,
  };
};
